import { useRef, useEffect } from 'react';

import { usePluginStateEffect } from '@atlaskit/editor-common/use-plugin-state-effect';
import type { Slice } from '@atlaskit/editor-prosemirror/model';

import { fg } from '@confluence/feature-gating';

import { extractLinksFromSlice } from './link-detector.tsx';
import { useDetectPastedThirdPartyLinks } from './useDetectPastedThirdPartyLinks.ts';

/**
 * Minimal type for paste plugin state (from editorAPI paste plugin).
 */
type PasteState = {
	lastContentPasted?: { pastedSlice: Slice } | null;
};

/**
 * Full-page-editor-specific hook for detecting newly pasted third-party links.
 *
 * Uses editorAPI paste plugin state to detect pasted links.
 * Early return for non-live pages, which use the onPublish path.
 *
 */
export const useDetectPastedThirdPartyLinksForFullPageEditor = (
	editorAPI: Parameters<typeof usePluginStateEffect>[0] | undefined,
	isLivePage: boolean,
) => {
	const { handlePastedLinks, handleSave, setInitialLinks } = useDetectPastedThirdPartyLinks();

	const hasSetBaselineRef = useRef(false);

	// Capture starting links for the full page editor
	useEffect(() => {
		if (!editorAPI?.core?.actions?.requestDocument || !fg('twcg-853-3p-in-1p-aa-test-gate')) {
			return;
		}

		editorAPI.core.actions.requestDocument(
			(doc) => {
				if (hasSetBaselineRef.current) {
					return;
				}
				hasSetBaselineRef.current = true;
				setInitialLinks(doc as any);
			},
			{ alwaysFire: true },
		);
	}, [editorAPI, setInitialLinks]);

	// On paste: extract links from ONLY the pasted slice (not the entire document).
	// Returns early for non-live pages — those use the publish-time handleSave path
	// in usePublish instead.
	usePluginStateEffect(editorAPI, ['paste'], ({ pasteState }: { pasteState?: PasteState }) => {
		if (!isLivePage) {
			return;
		}
		const lastPasted = pasteState?.lastContentPasted;
		if (!lastPasted?.pastedSlice || !fg('twcg-853-3p-in-1p-aa-test-gate')) {
			return;
		}

		const pastedUrls: string[] = extractLinksFromSlice(lastPasted.pastedSlice);
		handlePastedLinks(pastedUrls);
	});

	return { handleSave };
};
